import React, { useEffect, useState } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  deleteBusiness,
  getAllBusiness,
  getUserPlan,
} from "../../services/services";
import { toast } from "react-toastify";
import { toastError } from "../../utils";
import Loading from "../../components/loading";
import "./Business.css";
import { Link, useNavigate } from "react-router-dom";
import { confirmDelete } from "../../components/confirmDelete";
import { first } from "lodash";
import { deleteBusinessFromState } from "../../redux/business/businessSlice";
import { useDispatch } from "react-redux";

function Business() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("Login_User"));
  const activeBusiness = JSON.parse(localStorage.getItem("selectedBusiness"));

  const fetchBusinesses = async () => {
    try {
      const res = await getAllBusiness(user?._id);
      const data = res.data;

      if (data.status) {
        let list = data.response || [];

        if (activeBusiness?._id) {
          const active = list.find((b) => b._id === activeBusiness._id);

          const rest = list.filter((b) => b._id !== activeBusiness._id);

          list = active ? [active, ...rest] : list;
        }

        setBusinesses(list);

        setSelectedBusiness(
          list.find((b) => b._id === activeBusiness?._id) || list[0] || null,
        );

        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
      toast.error("Failed to load businesses");
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (activeBusiness?._id === id) {
      toast.error("You cannot delete the active business");
      return;
    }
    const confirm = await confirmDelete(`Business ${name}`);
    if (!confirm) return;

    if (!activeBusiness) {
      localStorage.setItem(
        "selectedBusiness",
        JSON.stringify(first(businesses)),
      );
    }
    setLoading(true);
    try {
      const res = await deleteBusiness(id);
      if (res.data.status) {
        toast.success(res.data.message);

        const updatedBusinesses = businesses.filter((b) => b._id !== id);
        setBusinesses(updatedBusinesses);

        dispatch(deleteBusinessFromState(id));

        // Update selected business
        if (selectedBusiness?._id === id) {
          setSelectedBusiness(updatedBusinesses[0] || null);
        }

        // Optional: update localStorage if needed
        const active = JSON.parse(localStorage.getItem("selectedBusiness"));
        if (active?._id === id) {
          localStorage.setItem(
            "selectedBusiness",
            JSON.stringify(updatedBusinesses[0] || null),
          );
        }
      } else {
        toastError(res.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = async () => {
    try {
      const res = await getUserPlan();

      if (!res?.data?.status) {
        toast.error("Failed to fetch plan details");
        return;
      }

      const data = res.data.response || {};
      const businessCount = data.businesses?.length || 0;

      const plan = data.plan;

      // Strong safety checks
      if (!plan?.planId?.features) {
        toast.error(
          "You have reached the maximum number of businesses. Please upgrade your plan.",
        );
        return;
      }

      const planDetails = plan.planId;

      // Find Multiple Business feature
      const businessFeature = planDetails.features.find(
        (f) => f.name === "Multiple Business",
      );

      if (!businessFeature?.value) {
        toast.error(
          "Multiple business feature is not available in your current plan.",
        );
        return;
      }

      const limit = Number(businessFeature.value);

      if (businessCount >= limit) {
        toast.error(
          `You have reached the maximum limit of ${limit} businesses. Please upgrade your plan.`,
        );
        return;
      }

      // All good → Navigate
      navigate("/add-business");
    } catch (error) {
      console.error("Error fetching user plan:", error);
      toast.error("Something went wrong while checking your plan.");
    }
  };

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <section id="main" className="business-container">
      <div className="d-flex justify-content-between mb-4 mt-2">
        <h4 className="custom-title mb-0">Manage Business</h4>
        <button className="btn btn-sm btn-primary" onClick={handleNavigate}>
          Add New Business
        </button>
      </div>

      <div className="row h-100 g-4">
        {/* Left Side - Business List */}
        <div className="col-lg-4 col-md-5 col-12">
          <div className="business-list-header">
            <h5 className="mb-0">Businesses</h5>
          </div>
          <div className="business-list">
            {businesses.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No businesses found</p>
              </div>
            ) : (
              [...businesses]
                .sort((a, b) => {
                  if (a._id === activeBusiness?._id) return -1;
                  if (b._id === activeBusiness?._id) return 1;
                  return 0;
                })
                .map((business) => (
                  <div
                    key={business._id}
                    className={`business-list-item ${
                      selectedBusiness?._id === business._id ? "active" : ""
                    }`}
                    onClick={() => handleBusinessClick(business)}
                  >
                    <div className="business-item-content">
                      <h6 className="business-name mb-1">
                        {business.businessName}
                      </h6>
                      <p className="business-email mb-0 small">
                        {business.email}
                      </p>
                    </div>
                    {activeBusiness?._id == business?._id && (
                      <div className="badge bg-success">Active</div>
                    )}
                  </div>
                ))
            )}
            {/* <li className="dropdown-header">
              <h6
                className={`mt-4 mb-0 text-wrap ${
                  activeBusiness ? "text-success" : ""
                }`}
              >
                {activeBusiness
                  ? `Active: ${activeBusiness.businessName}`
                  : "No Active Business"}
              </h6>
            </li> */}
          </div>
        </div>

        {/* Right Side - Business Details */}
        <div className="col-lg-8 col-md-7 col-12">
          {selectedBusiness ? (
            <div className="business-details">
              <div className="business-details-header">
                <h5 className="mb-0">Business Details</h5>
                <div className="business-action-buttons">
                  <button
                    className="btn btn-info me-3"
                    onClick={() =>
                      navigate(`/add-business/${selectedBusiness._id}`)
                    }
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(
                        selectedBusiness._id,
                        selectedBusiness.businessName,
                      );
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="business-details-content px-0">
                <div className="row g-4 px-4">
                  <div className="col-md-4">
                    <div className="detail-item">
                      <label>Business Logo</label>
                      {selectedBusiness.logo ? (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_URL}/uploads/business/${selectedBusiness.logo}`}
                          alt="logo"
                          style={{
                            height: "50%",
                            width: "50%",
                            objectFit: "cover",
                            borderRadius: "7%",
                          }}
                        />
                      ) : (
                        <i style={{ fontSize: "14px" }}>Logo not available</i>
                      )}
                    </div>
                    <div className="detail-item">
                      <label>Signature</label>
                      {selectedBusiness.signature ? (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_URL}/uploads/business/${selectedBusiness.signature}`}
                          alt="signature"
                          style={{
                            height: "50%",
                            width: "50%",
                            objectFit: "cover",
                            borderRadius: "7%",
                          }}
                        />
                      ) : (
                        <i style={{ fontSize: "14px" }}>
                          Signature not available
                        </i>
                      )}
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="detail-item">
                          <label>Business Name</label>
                          <p className="detail-value">
                            {selectedBusiness.businessName}
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>Email</label>
                          <p className="detail-value">
                            {selectedBusiness.email || "-"}
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>Phone</label>
                          <p className="detail-value">
                            {selectedBusiness.phone || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="detail-item">
                          <label>Business Type</label>
                          <p className="detail-value capitalize">
                            {selectedBusiness.businessType}
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>Category</label>
                          <p className="detail-value capitalize">
                            {selectedBusiness.category || "-"}
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>GSTIN</label>
                          <p className="detail-value">
                            {selectedBusiness.gstNumber || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="business-address-card mt-4">
                  <h6 className="address-title">
                    <i className="bi bi-geo-alt-fill me-2"></i> Address Details
                  </h6>

                  <div className="row g-4">
                    <div className="col-sm-6">
                      <div className="detail-item">
                        <label>City</label>
                        <p className="detail-value">
                          {selectedBusiness.city || "-"}
                        </p>
                      </div>{" "}
                      <div className="detail-item">
                        <label>Country</label>
                        <p className="detail-value capitalize">
                          {selectedBusiness.country || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="detail-item">
                        <label>State</label>
                        <p className="detail-value capitalize">
                          {selectedBusiness.state || "-"}
                        </p>
                      </div>

                      <div className="detail-item">
                        <label>PIN Code</label>
                        <p className="detail-value">
                          {selectedBusiness.pin || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="col-sm-12">
                      <div className="detail-item">
                        <label>Full Address</label>
                        <p className="detail-value">
                          {selectedBusiness.address || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Information Section */}
                {/* {selectedBusiness.user && (
                  <div className="user-section mt-4">
                    <h6 className="section-title">User Information</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="detail-item">
                          <label>User Name</label>
                          <p className="detail-value">
                            {selectedBusiness.user.name}
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>User Email</label>
                          <p className="detail-value">
                            {selectedBusiness.user.email}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="detail-item">
                          <label>User Phone</label>
                          <p className="detail-value">
                            {selectedBusiness.user.phone}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          ) : (
            <div className="no-selection text-center">
              <div className="no-selection-content">
                <i className="fas fa-building fa-3x text-muted mb-3"></i>
                <h5>No Business Selected</h5>
                <p className="text-muted">
                  Select a business from the list to view details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default headerLayoutWrapper(Business);
